/**
 * Per-level VisualProfiles composed from presets + level-specific overrides.
 *
 * Each profile is built by spreading a light rig preset and a post-process
 * preset, then adding the level's unique clear color, fog, glow intensity,
 * and named color palette.
 *
 * Palette colors come from the existing GameConfig COLORS and per-level
 * COLORS sections -- they are NOT duplicated here, just referenced by
 * hex string and converted to Color3/Color4 for type safety.
 */
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import type { VisualProfile } from "../VisualProfile";

// Light rig presets
import {
  WARM_INTERIOR,
  TWILIGHT_FOREST,
  CONSERVATORY_GLASSHOUSE,
  STARLIT_CANOPY,
  DARK_MENU,
} from "./light-rigs";

// Post-process presets
import {
  SOFT_GLOW,
  DENSE_ATMOSPHERE,
  UNDERGROUND_WARMTH,
  CINEMATIC_NIGHT,
} from "./post-process";

// Existing palette constants from GameConfig
import {
  COLORS,
  SCRAPYARD,
  OVERGROWN_CONSERVATORY,
  CLOCKWORK_CANOPY,
} from "../../config/GameConfig";

// ── Title Screen ───────────────────────────────────────────────────────
// Source: TitleScreen.ts
// Minimal dark scene with bioluminescent accents. No fog, no post-process.

export const TITLE_SCREEN_PROFILE: VisualProfile = {
  clearColor: new Color4(0.02, 0.01, 0.05, 1.0),
  lightRig: DARK_MENU,
  postProcess: {},                             // Title screen has no pipeline
  glowIntensity: 0.6,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    midnightBlue: Color3.FromHexString(COLORS.MIDNIGHT_BLUE),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    bioPurple: Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
  },
};

// ── Level Select ───────────────────────────────────────────────────────
// Source: LevelSelect.ts
// Very similar to title screen but slightly brighter ambient.

export const LEVEL_SELECT_PROFILE: VisualProfile = {
  clearColor: new Color4(0.03, 0.015, 0.05, 1.0),
  lightRig: {
    ...DARK_MENU,
    ambient: { ...DARK_MENU.ambient, intensity: 0.4 },
  },
  postProcess: {},
  glowIntensity: 0.6,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    bioPurple: Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
  },
};

// ── Level 0: Liminal Atrium ────────────────────────────────────────────
// Source: LiminalAtrium.ts
// Victorian sunroom: light enters from the garden; subdued room bounce.

export const LIMINAL_ATRIUM_PROFILE: VisualProfile = {
  clearColor: new Color4(0.06, 0.04, 0.09, 1.0),
  fog: {
    color: new Color3(0.23, 0.29, 0.25),
    density: 0.003,
  },
  lightRig: {
    ...WARM_INTERIOR,
    ambient: {
      ...WARM_INTERIOR.ambient,
      color: new Color3(0.64, 0.69, 0.65),
      groundColor: new Color3(0.26, 0.20, 0.12),
      intensity: 0.7,
    },
    key: { ...WARM_INTERIOR.key, direction: new Vector3(0.45, -1, 0.55), intensity: 3.2, shadowBlurKernel: 8 },
  },
  postProcess: {
    ...SOFT_GLOW,
    vignette: { ...SOFT_GLOW.vignette!, weight: 0.65 },
    imageProcessing: { contrast: 1.05, exposure: 1.15 },
    colorGrading: { globalSaturation: -8, globalDensity: -8 },
  },
  glowIntensity: 0.22,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    midnightBlue: Color3.FromHexString(COLORS.MIDNIGHT_BLUE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    ground: Color3.FromHexString(COLORS.GROUND),
    platform: Color3.FromHexString(COLORS.PLATFORM),
  },
};

// ── Level 1: Redwood Scrapyard ─────────────────────────────────────────
// Source: RedwoodScrapyard.ts
// Twilight forest: amber filtered sunlight, dense atmosphere, low fog.

export const REDWOOD_SCRAPYARD_PROFILE: VisualProfile = {
  clearColor: new Color4(0.25, 0.34, 0.28, 1.0),
  fog: {
    color: new Color3(0.30, 0.39, 0.32),
    density: 0.009,
  },
  lightRig: {
    ...TWILIGHT_FOREST,
    ambient: {
      color: new Color3(0.55, 0.64, 0.55),
      specular: new Color3(0.15, 0.17, 0.14),
      groundColor: new Color3(0.22, 0.18, 0.12),
      intensity: 0.85,
    },
    key: {
      ...TWILIGHT_FOREST.key,
      direction: new Vector3(0.3, -1, -0.45),
      color: new Color3(0.95, 0.85, 0.67),
      intensity: 3.0,
      shadowBlurKernel: 32,
      shadowKernelBlur: true,
      shadowDarkness: 0.35,
      shadowHighPrecision: true,
    },
    accents: [],
  },
  postProcess: {
    ...DENSE_ATMOSPHERE,
    bloom: { threshold: 0.8, weight: 0.12, kernel: 32 },
    vignette: { weight: 0.65, cameraFov: 0.5, color: new Color4(0.025, 0.04, 0.025, 1) },
    imageProcessing: { contrast: 1.05, exposure: 1.1 },
    colorGrading: { globalSaturation: -10, globalDensity: -6 },
  },
  glowIntensity: 0.22,
  palette: {
    skyTop: Color3.FromHexString(SCRAPYARD.COLORS.SKY_TOP),
    skyBottom: Color3.FromHexString(SCRAPYARD.COLORS.SKY_BOTTOM),
    redwoodDark: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK),
    redwoodBark: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_BARK),
    moss: Color3.FromHexString(SCRAPYARD.COLORS.MOSS),
    rust: Color3.FromHexString(SCRAPYARD.COLORS.RUST),
    brass: Color3.FromHexString(COLORS.BRASS),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
  },
};

// ── Level 2: Overgrown Conservatory ────────────────────────────────────
// Source: OvergrownConservatory.ts
// Underground bakery: warm oven glow, candle accents, brown vignette.

export const OVERGROWN_CONSERVATORY_PROFILE: VisualProfile = {
  // Cutaway Victorian glasshouse: readable materials, cool garden depth,
  // local working lamps and bounded skin shadows. The cycle owns the
  // restrained warm/cool directional and ambient changes.
  clearColor: new Color4(0.07, 0.11, 0.14, 1.0),
  fog: {
    // Depth beyond the rear windows; no foreground fog curtain.
    color: new Color3(0.16, 0.33, 0.38),
    density: 0.006,
  },
  lightRig: CONSERVATORY_GLASSHOUSE,
  postProcess: UNDERGROUND_WARMTH,
  glowIntensity: 0.4,
  palette: {
    amberWarm: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.AMBER_WARM),
    kitchenCopper: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.KITCHEN_COPPER),
    burrowEarth: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_EARTH),
    burrowDeep: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_DEEP),
    midnightIndigo: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.MIDNIGHT_INDIGO),
    midnightDeep: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.MIDNIGHT_DEEP),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
  },
};

// ── Level 3: Clockwork Canopy ──────────────────────────────────────────
// Source: ClockworkCanopy.ts
// Starlit treetops: cool moonlight, brass accents, cinematic vignette.

export const CLOCKWORK_CANOPY_PROFILE: VisualProfile = {
  clearColor: new Color4(0.02, 0.04, 0.1, 1.0),
  fog: {
    color: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    density: 0.008,
  },
  // The opaque treetop sky owns the night silhouette. Neutral moonlight
  // preserves black fur, tabby stripes and timber rather than crushing
  // the entire playable corridor with blue ambient and dense grading.
  lightRig: {
    ...STARLIT_CANOPY,
    ambient: { ...STARLIT_CANOPY.ambient,
      color: new Color3(.58, .65, .73), groundColor: new Color3(.18, .16, .14), intensity: 1.15 },
    // A frontward moon angle reveals fur and the faces of thin brasswork;
    // retain the same power and palette rather than lifting exposure.
    key: { ...STARLIT_CANOPY.key, direction: new Vector3(-.35, -1, -.9), color: new Color3(.82, .88, .96),
      specular: new Color3(.6, .65, .72), intensity: 2.1,
      shadowHighPrecision: true, shadowKernelBlur: true,
      shadowBlurKernel: 24, shadowDarkness: .35 },
  },
  postProcess: {
    ...CINEMATIC_NIGHT,
    bloom: { threshold: .8, weight: .16, kernel: 32 },
    vignette: { weight: .65, cameraFov: .7, color: new Color4(.01, .015, .03, 1) },
    imageProcessing: { contrast: 1.08, exposure: 1.1 },
    colorGrading: { globalSaturation: -4, globalDensity: 0 },
  },
  glowIntensity: .25,
  palette: {
    starlitSky: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.STARLIT_SKY),
    midnightBlue: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.MIDNIGHT_BLUE),
    brassWarm: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM),
    goldFog: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    copperDark: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.COPPER_DARK),
    lensRed: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_RED),
    lensGreen: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GREEN),
    lensBlue: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_BLUE),
    lensGold: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GOLD),
    constellation: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION),
    orreryBrass: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS),
    bioBlue: Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE),
    brass: Color3.FromHexString(COLORS.BRASS),
  },
};
